import React, { useState } from 'react';
import { 
  X, Bell, CheckCircle, Coffee, Shield, Trophy, Star, Megaphone, Sparkles, BarChart2, Check, ExternalLink, HelpCircle 
} from 'lucide-react';

const iconMap = {
  'bell': Bell,
  'check-circle': CheckCircle,
  'coffee': Coffee,
  'shield': Shield,
  'trophy': Trophy,
  'star': Star,
  'megaphone': Megaphone,
  'sparkles': Sparkles,
  'bar-chart-2': BarChart2,
};

const NewsCenterPanel = ({ isOpen, onClose, notifications, onMarkAsRead, onMarkAllAsRead }) => {
  const [activeTab, setActiveTab] = useState('personal'); // 'personal', 'group', 'community'

  if (!isOpen) return null;

  const getIconComponent = (iconName) => {
    const IconComponent = iconMap[iconName] || Bell;
    return <IconComponent size={18} style={{ color: 'var(--accent)' }} />;
  };

  // Filter notifications by tab
  const filteredNotifications = notifications.filter(n => {
    if (activeTab === 'personal') return n.category === 'personal';
    if (activeTab === 'group') return n.category === 'group';
    if (activeTab === 'community') return n.category === 'community';
    return false;
  });

  const unreadCount = filteredNotifications.filter(n => !n.is_read).length;

  const handleNotificationClick = (item) => {
    if (!item.is_read) {
      onMarkAsRead(item.id);
    }
    if (item.link) {
      window.location.href = item.link;
    }
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div 
        style={drawerStyle} 
        onClick={(e) => e.stopPropagation()}
        className="fade-in"
      >
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={bellContainerStyle}>
              <Bell size={20} color="var(--accent)" />
              {notifications.some(n => !n.is_read) && <span style={badgeDotStyle} />}
            </div>
            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-main)' }}>
              Site News & Notifications
            </h2>
          </div>
          <button onClick={onClose} style={closeButtonStyle} aria-label="Close panel">
            <X size={20} color="var(--text-soft)" />
          </button>
        </div>

        {/* Action Bar */}
        <div style={actionBarStyle}>
          <span style={{ fontSize: '0.85rem', color: 'var(--text-soft)' }}>
            {unreadCount} unread in this section
          </span>
          {unreadCount > 0 && (
            <button onClick={onMarkAllAsRead} style={markAllButtonStyle}>
              <Check size={14} style={{ marginRight: '4px' }} />
              Mark all as read
            </button>
          )}
        </div>

        {/* Tab Navigation */}
        <div style={tabContainerStyle}>
          <button 
            onClick={() => setActiveTab('personal')} 
            style={activeTab === 'personal' ? activeTabStyle : tabStyle}
          >
            Individuals
          </button>
          <button 
            onClick={() => setActiveTab('group')} 
            style={activeTab === 'group' ? activeTabStyle : tabStyle}
          >
            Circles
          </button>
          <button 
            onClick={() => setActiveTab('community')} 
            style={activeTab === 'community' ? activeTabStyle : tabStyle}
          >
            Sanctuary
          </button>
        </div>

        {/* Notifications List */}
        <div style={listContainerStyle}>
          {filteredNotifications.length === 0 ? (
            <div style={emptyStateStyle}>
              <HelpCircle size={36} color="var(--text-soft)" style={{ marginBottom: '12px', opacity: 0.5 }} />
              <p style={{ margin: 0, fontWeight: 500 }}>All Quiet Here</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.8rem', color: 'var(--text-soft)' }}>
                No notifications in this channel.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '4px' }}>
              {filteredNotifications.map((item) => (
                <div 
                  key={item.id} 
                  onClick={() => handleNotificationClick(item)}
                  style={{
                    ...cardStyle,
                    borderLeft: `4px solid ${item.is_read ? 'transparent' : 'var(--accent)'}`,
                    opacity: item.is_read ? 0.8 : 1,
                  }}
                  className="hover-card"
                >
                  <div style={cardHeaderStyle}>
                    <div style={iconBadgeStyle}>
                      {getIconComponent(item.icon)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                        <h4 style={{ 
                          margin: 0, 
                          fontSize: '0.9rem', 
                          fontWeight: item.is_read ? 500 : 600, 
                          color: 'var(--text-main)',
                          lineHeight: '1.2'
                        }}>
                          {item.title}
                        </h4>
                        {!item.is_read && <span style={unreadIndicatorStyle} />}
                      </div>
                      <p style={{ 
                        margin: '6px 0 0 0', 
                        fontSize: '0.82rem', 
                        color: 'var(--text-soft)', 
                        lineHeight: '1.4' 
                      }}>
                        {item.content}
                      </p>
                      
                      {/* Meta information & Link indication */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                        <span style={timeStyle}>
                          {new Date(item.created_at).toLocaleDateString(undefined, { 
                            month: 'short', 
                            day: 'numeric', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                        {item.link && (
                          <span style={linkIndicatorStyle}>
                            Go to <ExternalLink size={10} style={{ marginLeft: '2px' }} />
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Beautiful Cozy Glassmorphism Styles
const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.4)',
  backdropFilter: 'blur(4px)',
  zIndex: 1100,
  display: 'flex',
  justifyContent: 'flex-end',
};

const drawerStyle = {
  width: '100%',
  maxWidth: '420px',
  height: '100%',
  backgroundColor: 'var(--bg-card)',
  boxShadow: '-10px 0 30px rgba(0, 0, 0, 0.15)',
  display: 'flex',
  flexDirection: 'column',
  padding: '1.25rem',
  boxSizing: 'border-box',
  borderLeft: '1px solid var(--border)',
};

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  paddingBottom: '0.75rem',
  borderBottom: '1px solid var(--border)',
};

const bellContainerStyle = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '36px',
  height: '36px',
  borderRadius: '50%',
  backgroundColor: 'var(--accent-soft)',
};

const badgeDotStyle = {
  position: 'absolute',
  top: '8px',
  right: '8px',
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: '#ff9800',
  boxShadow: '0 0 0 2px var(--bg-card)',
};

const closeButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: '6px',
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'background-color 0.2s',
  ':hover': {
    backgroundColor: 'var(--border)',
  }
};

const actionBarStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '8px 0',
  borderBottom: '1px solid var(--border)',
};

const markAllButtonStyle = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.8rem',
  color: 'var(--accent)',
  display: 'flex',
  alignItems: 'center',
  fontWeight: '600',
  padding: '4px 8px',
  borderRadius: '6px',
  backgroundColor: 'var(--accent-soft)',
  transition: 'opacity 0.2s',
};

const tabContainerStyle = {
  display: 'flex',
  gap: '4px',
  padding: '8px 0',
  borderBottom: '1px solid var(--border)',
  marginBottom: '1rem',
};

const tabStyle = {
  flex: 1,
  padding: '10px 4px',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: '0.85rem',
  fontWeight: '500',
  color: 'var(--text-soft)',
  borderBottom: '2px solid transparent',
  transition: 'all 0.2s',
};

const activeTabStyle = {
  ...tabStyle,
  color: 'var(--accent)',
  fontWeight: '600',
  borderBottom: '2px solid var(--accent)',
};

const listContainerStyle = {
  flex: 1,
  overflowY: 'auto',
  paddingRight: '4px',
};

const emptyStateStyle = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  height: '250px',
  color: 'var(--text-soft)',
  textAlign: 'center',
};

const cardStyle = {
  padding: '12px',
  backgroundColor: 'var(--bg-app)',
  borderRadius: '12px',
  border: '1px solid var(--border)',
  cursor: 'pointer',
  transition: 'transform 0.2s, box-shadow 0.2s',
};

const cardHeaderStyle = {
  display: 'flex',
  gap: '12px',
  alignItems: 'flex-start',
};

const iconBadgeStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  borderRadius: '8px',
  backgroundColor: 'var(--accent-soft)',
  flexShrink: 0,
};

const unreadIndicatorStyle = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: 'var(--accent)',
  flexShrink: 0,
  marginTop: '4px',
};

const timeStyle = {
  fontSize: '0.72rem',
  color: 'var(--text-soft)',
};

const linkIndicatorStyle = {
  fontSize: '0.72rem',
  color: 'var(--accent)',
  fontWeight: '600',
  display: 'flex',
  alignItems: 'center',
};

export default NewsCenterPanel;
